import { useEffect, useState } from 'react';

import { datadogRum } from 'root/datadog/datadogRum';

export interface InvoicingErrorDetail {
  message: string;
  stack: string;
  componentStack: string;
}

export type ScriptLoader = 'systemjs' | 'module';

export function useInvoicingScript(
  scriptUrl: string,
  elementTag: string,
  loader: ScriptLoader = 'module'
): boolean {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const markReady = () => {
      if (!cancelled) setIsReady(true);
    };

    if (customElements.get(elementTag)) {
      setIsReady(true);
      return undefined;
    }

    if (loader === 'systemjs') {
      System.import(scriptUrl)
        .then(() => {
          customElements.whenDefined(elementTag).then(markReady);
        })
        .catch(() => {
          if (!cancelled) {
            datadogRum.addError(new Error(`Failed to load ${elementTag} script`));
          }
        });

      return () => {
        cancelled = true;
      };
    }

    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existingScript) {
      customElements.whenDefined(elementTag).then(markReady);
      return () => {
        cancelled = true;
      };
    }

    const script = document.createElement('script');
    script.type = 'module';
    script.src = scriptUrl;
    script.onload = () => {
      customElements.whenDefined(elementTag).then(markReady);
    };
    script.onerror = () => {
      if (!cancelled) {
        datadogRum.addError(new Error(`Failed to load ${elementTag} script`));
      }
    };
    document.head.appendChild(script);

    return () => {
      cancelled = true;
      if (!customElements.get(elementTag)) {
        document.head.removeChild(script);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; script URL and tag are static config
  }, []);

  return isReady;
}

export function createInvoicingErrorHandler(source: string, fallbackMessage?: string) {
  return (event: Event): void => {
    const detail = (event as CustomEvent<InvoicingErrorDetail>).detail;
    const error = new Error(detail?.message ?? fallbackMessage ?? `${source} error`);
    if (detail?.stack) error.stack = detail.stack;
    datadogRum.addError(error, {
      componentStack: detail?.componentStack,
      source,
    });
  };
}
